import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { exportFilename } from "@/lib/timefmt";
import { reportImportErrors } from "@/lib/importUtils";
import { Plus, Trash2, Pencil, Download, Upload } from "lucide-react";

export default function EquipmentTab() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [units, setUnits] = useState([]);
  const [tree, setTree] = useState({ units: [] });
  const [cfg, setCfg] = useState({ types: [], equipment: [] });
  const [typeFor, setTypeFor] = useState(undefined); // undefined=closed, null=new, object=edit
  const [itemFor, setItemFor] = useState(undefined);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => { setCompanies(r.data); if (r.data[0]) setCompanyId((p) => p || r.data[0].id); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCfg = () => api.get(`/equipment-config?company_id=${companyId}`).then((r) => setCfg(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  useEffect(() => {
    if (!companyId) return;
    api.get(`/org/tree?company_id=${companyId}`).then((r) => { setUnits(r.data.units || []); setTree(r.data); }).catch(() => {});
    loadCfg();
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const unitName = (id) => units.find((u) => u.id === id)?.name || "—";
  const orgPath = (e) => {
    const names = [];
    for (const u of tree.units || []) {
      if (u.id === e.unit_id) names.push(u.name);
      for (const s of u.subunits || []) {
        if (s.id === e.subunit_id) names.push(s.name);
        for (const t of s.teams || []) if (t.id === e.team_id) names.push(t.name);
      }
    }
    return names.length ? names.join(" / ") : "Company-wide";
  };
  const exportCsv = async (fmt) => {
    try {
      const res = await api.get("/admin/equipment/export", { params: { company_id: companyId, fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFilename("crew-ready-equipment", fmt, user);
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="equipment-tab">
      <div className="flex items-center gap-3 flex-wrap">
        {companies.length > 1 && (
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="equipment-company-select"
            className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <button onClick={() => exportCsv("csv")} data-testid="equipment-export"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs">
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
        <button onClick={() => exportCsv("xlsx")} data-testid="equipment-export-xlsx"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs">
          <Download className="h-3.5 w-3.5" /> Export XLSX
        </button>
        <button onClick={() => setImportOpen(true)} data-testid="equipment-import-open"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs">
          <Upload className="h-3.5 w-3.5" /> Import CSV
        </button>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="equipment-types-card">
        <div className="flex items-center justify-between mb-1">
          <div className="font-heading text-lg font-semibold">Equipment Types</div>
          <button onClick={() => setTypeFor(null)} data-testid="eq-type-add-open"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs">
            <Plus className="h-3.5 w-3.5" /> Add type
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-3">Name the equipment types used in this company (e.g. Aircraft, Vehicle) with their subtypes (e.g. CH-47, MRH-90) and the org units they apply to.</p>
        <div className="divide-y divide-slate-800">
          {cfg.types.length === 0 && <div className="py-3 text-sm text-slate-500">No equipment types yet.</div>}
          {cfg.types.map((t) => (
            <div key={t.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`eq-type-row-${t.id}`}>
              <div className="text-sm">
                {t.name}
                <span className="ml-2 text-[10px] text-slate-500">{(t.subtypes || []).join(", ") || "no subtypes"}</span>
                <span className="ml-2 text-[10px] text-slate-500">{t.unit_ids?.length ? t.unit_ids.map(unitName).join(", ") : "All units"}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button onClick={() => setTypeFor(t)} data-testid={`eq-type-edit-${t.id}`}
                  className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
                <button onClick={async () => { if (window.confirm(`Delete type "${t.name}" and its equipment?`)) { await api.delete(`/admin/equipment-types/${t.id}`); loadCfg(); toast.success("Type deleted"); } }}
                  data-testid={`eq-type-del-${t.id}`} className="text-xs text-rose-400 hover:text-rose-300 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="equipment-items-card">
        <div className="flex items-center justify-between mb-1">
          <div className="font-heading text-lg font-semibold">Equipment</div>
          <button onClick={() => setItemFor(null)} data-testid="eq-add-open" disabled={cfg.types.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs disabled:opacity-40">
            <Plus className="h-3.5 w-3.5" /> Add equipment
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-3">Individual equipment items (e.g. tail number A15-302) with config/remarks in Detail, optionally assigned to an org unit.</p>
        <div className="divide-y divide-slate-800">
          {cfg.equipment.length === 0 && <div className="py-3 text-sm text-slate-500">No equipment yet.</div>}
          {cfg.equipment.map((e) => (
            <div key={e.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`eq-row-${e.id}`}>
              <div className="text-sm">
                {e.label}
                {e.detail && <span className="ml-2 text-[10px] text-slate-500 italic">{e.detail}</span>}
                {e.capacity && <span className="ml-2 text-[10px] font-mono-num text-emerald-400/80">{e.capacity} pax</span>}
                <span className="ml-2 text-[10px] text-slate-500">{orgPath(e)}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button onClick={() => setItemFor(e)} data-testid={`eq-edit-${e.id}`}
                  className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
                <button onClick={async () => { if (window.confirm(`Delete "${e.label}"?`)) { await api.delete(`/admin/equipment/${e.id}`); loadCfg(); toast.success("Equipment deleted"); } }}
                  data-testid={`eq-del-${e.id}`} className="text-xs text-rose-400 hover:text-rose-300 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <TypeDialog type={typeFor} companyId={companyId} units={units}
        onClose={() => setTypeFor(undefined)} onDone={() => { setTypeFor(undefined); loadCfg(); }} />
      <ItemDialog item={itemFor} companyId={companyId} types={cfg.types} tree={tree}
        onClose={() => setItemFor(undefined)} onDone={() => { setItemFor(undefined); loadCfg(); }} />
      <ImportDialog open={importOpen} companyId={companyId}
        onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); loadCfg(); }} />
    </div>
  );
}

function TypeDialog({ type, companyId, units, onClose, onDone }) {
  const [name, setName] = useState("");
  const [subtypes, setSubtypes] = useState("");
  const [unitIds, setUnitIds] = useState([]);
  useEffect(() => {
    if (type === undefined) return;
    setName(type?.name || "");
    setSubtypes((type?.subtypes || []).join(", "));
    setUnitIds(type?.unit_ids || []);
  }, [type]);
  if (type === undefined) return null;
  const submit = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    const payload = { company_id: companyId, name: name.trim(), subtypes: subtypes.split(",").map((s) => s.trim()).filter(Boolean), unit_ids: unitIds };
    try {
      if (type?.id) await api.put(`/admin/equipment-types/${type.id}`, payload);
      else await api.post("/admin/equipment-types", payload);
      toast.success("Equipment type saved");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="eq-type-dialog">
        <DialogHeader><DialogTitle className="font-heading">{type?.id ? "Edit equipment type" : "New equipment type"}</DialogTitle></DialogHeader>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Type name (e.g. Aircraft)</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="eq-type-name"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Subtypes (comma-separated, e.g. CH-47, MRH-90)</label>
        <input value={subtypes} onChange={(e) => setSubtypes(e.target.value)} data-testid="eq-type-subtypes"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Available to org units</label>
        <div className="flex flex-wrap gap-3" data-testid="eq-type-units">
          {units.map((u) => (
            <label key={u.id} className="flex items-center gap-1.5 text-xs text-slate-300">
              <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={unitIds.includes(u.id)}
                data-testid={`eq-type-unit-${u.id}`}
                onChange={() => setUnitIds(unitIds.includes(u.id) ? unitIds.filter((x) => x !== u.id) : [...unitIds, u.id])} />
              {u.name}
            </label>
          ))}
          {units.length > 0 && <span className="text-[10px] text-slate-600">No selection = all units</span>}
        </div>
        <button onClick={submit} data-testid="eq-type-save"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Save type</button>
      </DialogContent>
    </Dialog>
  );
}

function ItemDialog({ item, companyId, types, tree, onClose, onDone }) {
  const [typeId, setTypeId] = useState("");
  const [subtype, setSubtype] = useState("");
  const [number, setNumber] = useState("");
  const [detail, setDetail] = useState("");
  const [capacity, setCapacity] = useState("");
  const [orgSel, setOrgSel] = useState("");
  useEffect(() => {
    if (item === undefined) return;
    setTypeId(item?.type_id || "");
    setSubtype(item?.subtype || "");
    setNumber(item?.number || "");
    setDetail(item?.detail || "");
    setCapacity(item?.capacity ?? "");
    setOrgSel(item?.team_id ? `team:${item.team_id}` : item?.subunit_id ? `subunit:${item.subunit_id}` : item?.unit_id ? `unit:${item.unit_id}` : "");
  }, [item]);
  if (item === undefined) return null;
  const selType = types.find((t) => t.id === typeId);
  const orgOptions = [];
  for (const u of tree.units || []) {
    orgOptions.push({ value: `unit:${u.id}`, label: u.name });
    for (const s of u.subunits || []) {
      orgOptions.push({ value: `subunit:${s.id}`, label: `${u.name} / ${s.name}` });
      for (const t of s.teams || []) {
        orgOptions.push({ value: `team:${t.id}`, label: `${u.name} / ${s.name} / ${t.name}` });
      }
    }
  }
  const submit = async () => {
    if (!typeId) { toast.error("Pick a type"); return; }
    const [kind, oid] = orgSel ? orgSel.split(":") : [null, null];
    const payload = { company_id: companyId, type_id: typeId, subtype: subtype || null, number: number.trim() || null, detail: detail.trim() || null,
      unit_id: kind === "unit" ? oid : null, subunit_id: kind === "subunit" ? oid : null, team_id: kind === "team" ? oid : null,
      capacity: capacity === "" ? null : Number(capacity) };
    try {
      if (item?.id) await api.put(`/admin/equipment/${item.id}`, payload);
      else await api.post("/admin/equipment", payload);
      toast.success("Equipment saved");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="eq-dialog">
        <DialogHeader><DialogTitle className="font-heading">{item?.id ? "Edit equipment" : "New equipment"}</DialogTitle></DialogHeader>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Type</label>
        <select value={typeId} onChange={(e) => { setTypeId(e.target.value); setSubtype(""); }} data-testid="eq-type-select"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3">
          <option value="">Select…</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {selType && (selType.subtypes || []).length > 0 && (
          <>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Subtype</label>
            <select value={subtype} onChange={(e) => setSubtype(e.target.value)} data-testid="eq-subtype-select"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3">
              <option value="">Select…</option>
              {selType.subtypes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </>
        )}
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Number (e.g. tail number)</label>
        <input value={number} onChange={(e) => setNumber(e.target.value)} data-testid="eq-number"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Detail (config, remarks)</label>
        <input value={detail} onChange={(e) => setDetail(e.target.value)} data-testid="eq-detail"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Capacity (persons) — limits how many can be on a task using it</label>
        <input type="number" min="1" value={capacity} onChange={(e) => setCapacity(e.target.value)} data-testid="eq-capacity" placeholder="No limit"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Allocated to (org structure)</label>
        <select value={orgSel} onChange={(e) => setOrgSel(e.target.value)} data-testid="eq-unit"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3">
          <option value="">Company-wide</option>
          {orgOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button onClick={submit} data-testid="eq-save"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Save equipment</button>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ open, companyId, onClose, onDone }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setText(""); }, [open]);
  if (!open) return null;
  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setText(String(r.result || ""));
    r.readAsText(f);
    e.target.value = "";
  };
  const submit = async () => {
    if (!text.trim()) { toast.error("Paste CSV content or pick a file first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/equipment/import", { company_id: companyId, text });
      toast.success(`Imported ${data.equipment} equipment item(s), ${data.types} new type(s)`
        + (data.skipped_duplicates ? ` — skipped ${data.skipped_duplicates} duplicate(s)` : "")
        + (data.skipped_invalid ? ` — ${data.skipped_invalid} invalid row(s)` : ""));
      reportImportErrors(data, "equipment", user);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="equipment-import-dialog">
        <DialogHeader><DialogTitle className="font-heading">Import equipment (CSV)</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 mb-1">Columns: <span className="font-mono-num">Type, Subtype, Number, Detail, Org Unit, Capacity</span> (Capacity optional)</p>
        <ul className="text-xs text-slate-500 mb-3 list-disc pl-4 space-y-0.5">
          <li>Types and subtypes are created automatically when first seen.</li>
          <li>A row with only a Type just ensures the type exists (no item created).</li>
          <li>Org Unit is optional and matched by name — equipment without one is company-wide.</li>
          <li>Rows matching an existing Type + Subtype + Number are skipped as duplicates.</li>
        </ul>
        <input type="file" accept=".csv,text/csv,text/plain" onChange={pickFile} data-testid="equipment-import-file"
          className="block w-full text-xs text-slate-400 mb-2 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700 file:cursor-pointer" />
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} data-testid="equipment-import-text"
          placeholder={"Type,Subtype,Number,Detail,Org Unit\nAircraft,CH-47,A15-302,NVG compatible,5 Avn"}
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm font-mono-num" />
        <button onClick={submit} disabled={busy} data-testid="equipment-import-btn"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2">
          <Upload className="h-4 w-4" /> {busy ? "Importing…" : "Import"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
